import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '@dukene/shared';
import {
  DollarSign,
  Store,
  ShoppingCart,
  Users,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';

type TimeRange = 'today' | 'week' | 'month' | 'year';

interface PlatformStats {
  totalRevenue: number;
  revenueChange: number;
  activeStores: number;
  storesChange: number;
  totalOrders: number;
  ordersChange: number;
  totalUsers: number;
  usersChange: number;
}

interface RevenueData {
  date: string;
  revenue: number;
  orders: number;
}

interface StorePerformance {
  id: string;
  name: string;
  logo?: string;
  revenue: number;
  orders: number;
  products: number;
  status: string;
}

interface UserGrowth {
  date: string;
  totalUsers: number;
  newUsers: number;
}

interface OrdersByStatus {
  status: string;
  count: number;
  percentage: number;
}

export default function Analytics() {
  const [timeRange, setTimeRange] = useState<TimeRange>('month');

  // Fetch platform stats
  const { data: stats, isLoading: statsLoading } = useQuery<PlatformStats>({
    queryKey: ['platform-stats', timeRange],
    queryFn: async () => {
      return apiClient.get<PlatformStats>(`/analytics/admin/stats?range=${timeRange}`);
    },
  });

  // Fetch revenue data
  const { data: revenueData, isLoading: revenueLoading } = useQuery<RevenueData[]>({
    queryKey: ['revenue-data', timeRange],
    queryFn: async () => {
      return apiClient.get<RevenueData[]>(`/analytics/admin/revenue?range=${timeRange}`);
    },
  });

  // Fetch top performing stores
  const { data: topStores, isLoading: storesLoading } = useQuery<StorePerformance[]>({
    queryKey: ['top-stores', timeRange],
    queryFn: async () => {
      return apiClient.get<StorePerformance[]>(`/analytics/admin/top-stores?range=${timeRange}`);
    },
  });

  // Fetch user growth data
  const { data: userGrowth, isLoading: userGrowthLoading } = useQuery<UserGrowth[]>({
    queryKey: ['user-growth', timeRange],
    queryFn: async () => {
      return apiClient.get<UserGrowth[]>(`/analytics/admin/user-growth?range=${timeRange}`);
    },
  });

  // Fetch orders by status
  const { data: ordersByStatus, isLoading: ordersStatusLoading } = useQuery<OrdersByStatus[]>({
    queryKey: ['orders-by-status', timeRange],
    queryFn: async () => {
      return apiClient.get<OrdersByStatus[]>(`/analytics/admin/orders-by-status?range=${timeRange}`);
    },
  });

  const isLoading = statsLoading || revenueLoading || storesLoading || userGrowthLoading || ordersStatusLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Revenue',
      value: `KES ${stats?.totalRevenue.toLocaleString() || 0}`,
      change: stats?.revenueChange || 0,
      icon: DollarSign,
    },
    {
      label: 'Active Stores',
      value: stats?.activeStores.toLocaleString() || 0,
      change: stats?.storesChange || 0,
      icon: Store,
    },
    {
      label: 'Total Orders',
      value: stats?.totalOrders.toLocaleString() || 0,
      change: stats?.ordersChange || 0,
      icon: ShoppingCart,
    },
    {
      label: 'Total Users',
      value: stats?.totalUsers.toLocaleString() || 0,
      change: stats?.usersChange || 0,
      icon: Users,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-black">Platform Analytics</h1>
          <p className="text-gray-500 mt-2">Overview of platform performance and growth</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as TimeRange)}
          className="h-12 rounded-xl border-2 border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black focus:border-black focus:outline-none transition-colors"
        >
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
          <option value="year">Last Year</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          const isPositive = stat.change >= 0;
          const TrendIcon = isPositive ? ArrowUpRight : ArrowDownRight;

          return (
            <div key={stat.label} className="group relative bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300">
              <div className="absolute top-6 right-6">
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="mt-16">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{stat.label}</p>
                <p className="text-5xl font-black text-black mt-2">{stat.value}</p>
                <div className="flex items-center gap-2 mt-3">
                  <TrendIcon className="w-4 h-4 text-black" />
                  <span className="text-sm font-medium text-black">{Math.abs(stat.change)}% from last period</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-black text-black mb-6">Platform Revenue</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={revenueData || []}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#000000" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#000000" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => format(new Date(value), 'MMM d')}
                stroke="#9ca3af"
              />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                formatter={(value: number) => [`KES ${value.toLocaleString()}`, 'Revenue']}
                contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '12px', color: '#fff' }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#000000"
                strokeWidth={2}
                fill="url(#colorRevenue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Orders Chart */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-black text-black mb-6">Orders Overview</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => format(new Date(value), 'MMM d')}
                stroke="#9ca3af"
              />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                formatter={(value: number) => [value, 'Orders']}
                contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '12px', color: '#fff' }}
              />
              <Bar dataKey="orders" fill="#000000" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* User Growth Chart */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-black text-black mb-6">User Growth</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userGrowth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => format(new Date(value), 'MMM d')}
                stroke="#9ca3af"
              />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '12px', color: '#fff' }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="totalUsers"
                stroke="#000000"
                strokeWidth={3}
                name="Total Users"
                dot={{ r: 5, fill: '#000000' }}
              />
              <Line
                type="monotone"
                dataKey="newUsers"
                stroke="#6b7280"
                strokeWidth={3}
                name="New Users"
                dot={{ r: 5, fill: '#6b7280' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Orders by Status */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-black text-black mb-6">Orders by Status</h2>
          <div className="space-y-4">
            {ordersByStatus && ordersByStatus.length > 0 ? (
              ordersByStatus.map((item) => (
                <div key={item.status}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-black uppercase tracking-wider">{item.status}</span>
                    <span className="text-sm font-medium text-gray-600">
                      {item.count} ({item.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="h-3 rounded-full bg-black transition-all"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-8">No order data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Top Performing Stores */}
      <div className="bg-white border border-gray-200 rounded-2xl p-8">
        <h2 className="text-xl font-black text-black mb-6">Top Performing Stores</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-900 uppercase tracking-wider">Rank</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-900 uppercase tracking-wider">Store</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-900 uppercase tracking-wider">Revenue</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-900 uppercase tracking-wider">Orders</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-900 uppercase tracking-wider">Products</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-900 uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-4 text-xs font-bold text-gray-900 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {topStores && topStores.length > 0 ? (
                topStores.slice(0, 10).map((store, index) => (
                  <tr key={store.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors last:border-0">
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white">
                        <span className="text-sm font-bold">#{index + 1}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {store.logo ? (
                          <img
                            src={store.logo}
                            alt={store.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Store className="w-5 h-5 text-black" />
                          </div>
                        )}
                        <span className="font-semibold text-black">{store.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-bold text-black">
                      KES {store.revenue.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-gray-700 font-medium">{store.orders.toLocaleString()}</td>
                    <td className="py-3 px-4 text-gray-700 font-medium">{store.products.toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold uppercase tracking-wider ${store.status === 'ACTIVE' ? 'bg-black text-white' :
                        store.status === 'SUSPENDED' ? 'bg-gray-700 text-white' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                        {store.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <Link
                        to={`/stores/${store.id}`}
                        className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-lg border-2 border-black text-black hover:bg-black hover:text-white transition-colors"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    No store data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
